/* De vaste pagina's van de site (website.pages, ADR 0024): één rij per pagina die in de code van de
 * site staat, zoals home, over-ons, contact en tools. De code van de site zoekt een rij op haar slug
 * (getPage in lib/content.ts van de site-repo). Een rij heeft geen publicatie: wat er staat, leest de
 * site. Daarom staat dit naast de soorten content in ./website en niet erin.
 *
 * De regels:
 *   - Er komt geen pagina bij en er gaat er geen weg. De slug en de naam worden nooit geschreven.
 *   - Per pagina staat hieronder wat de site met een kolom doet (site-repo, tak v2, 10-10-2026). Alleen
 *     een kolom die de site op die pagina toont, is te bewerken. De rest van de tekst van zo'n pagina
 *     staat in de code van de site.
 *   - Een pagina die hier niet in de lijst staat, is alleen te lezen: de CRM weet dan niet wat de site
 *     ermee doet.
 *   - Onaangeraakt is niet geschreven: alleen een gewijzigde kolom gaat naar de database.
 *   - `content` (jsonb): alleen de tekening van de homepage (`integrale_image`) is te bewerken. Wat er
 *     geschreven wordt, is het object uit de database met alleen die sleutel vervangen. Elke andere
 *     sleutel blijft letterlijk staan. Is `content` geen object, dan wordt het nooit geschreven.
 *
 * De client komt als argument binnen, zoals in ./website, zodat de toetsen hem kunnen nabootsen. */

import { keurBeeldpad } from './beeldpad';
import type { Controle, Rij, WebsiteClient } from './website';

export const TABEL = 'pages';
export const INHOUD = 'content';
/** De sleutel in `content` van de homepage met het pad van de tekening. */
export const TEKENING_SLEUTEL = 'integrale_image';
/** De sleutel waaronder een fout bij de tekening staat (Controle.fouten). */
export const TEKENING_FOUT = `${INHOUD}.${TEKENING_SLEUTEL}`;

/* De bewegende laag over de tekening op de homepage (StroomLaag.tsx in de site-repo) is opgemeten op
 * dit ene bestand. De site toont de laag alleen als het pad uit de eigen media komt en op deze naam
 * eindigt; bij elk ander beeld staat er alleen het beeld. */
export const TEKENING_BESTAND = '1772058634147-Energieoplossingen-3000-x-2000-px-3-1024x683.png';

/** Loopt de stroom over dit beeld? Dezelfde regel als de site: ownMedia(pad)?.endsWith('/' + naam). */
export const stroomLoopt = (pad: unknown): boolean => {
  const p = typeof pad === 'string' ? pad.trim() : '';
  return p.startsWith('/media/') && p.endsWith(`/${TEKENING_BESTAND}`);
};

export type Kolom = 'hero_title' | 'hero_subtitle' | 'hero_image';

export interface Veld { kolom: Kolom; label: string; soort: 'tekst' | 'afbeelding' }

export const VELDEN: Veld[] = [
  { kolom: 'hero_title', label: 'Kop', soort: 'tekst' },
  { kolom: 'hero_subtitle', label: 'Regel onder de kop', soort: 'tekst' },
  { kolom: 'hero_image', label: 'Foto bovenaan', soort: 'afbeelding' },
];

/** Wat de site op één pagina met één kolom doet. Alleen een kolom die de site toont, is te bewerken. */
export interface Gebruik { toont: boolean; uitleg: string }

export interface PaginaKennis {
  slug: string;
  /** Het adres op de site, of null als de site deze pagina niet meer toont. */
  pad: string | null;
  velden: Record<Kolom, Gebruik>;
  /** De tekening in `content` is hier te bewerken (alleen de homepage). */
  tekening?: boolean;
  /** Een zin over de pagina als geheel, boven het formulier. */
  uitleg?: string;
}

const toont = (uitleg: string): Gebruik => ({ toont: true, uitleg });
const toontNiet = (uitleg: string): Gebruik => ({ toont: false, uitleg });

const KOP_IN_CODE = toontNiet('De kop van deze pagina staat in de code van de site. Wat hier staat, toont de site niet.');
const REGEL_IN_CODE = toontNiet('De regel onder de kop staat in de code van de site. Wat hier staat, toont de site niet.');
const FOTO = toont('De foto bovenaan de pagina, achter de kop. Leeg betekent: de vaste foto uit de code van de site.');
const FOTO_EN_LINK = toont('De foto bovenaan de pagina, achter de kop, en het beeld bij een gedeelde link. Leeg betekent: de vaste foto uit de code van de site, en het logo bij een gedeelde link.');
const KOP_TOOL = toont('De kop in de foto bovenaan. De site zet hem in hoofdletters. Leeg betekent: de vaste kop uit de code van de site.');
const REGEL_TOOL = toont('De regel onder de kop. De site zet hem in hoofdletters. Leeg betekent: de vaste regel uit de code van de site.');
const NERGENS = toontNiet('De site leest deze rij niet meer.');

const alleenFoto = (slug: string, pad: string, foto: Gebruik = FOTO): PaginaKennis =>
  ({ slug, pad, velden: { hero_title: KOP_IN_CODE, hero_subtitle: REGEL_IN_CODE, hero_image: foto } });

const tool = (slug: string, pad: string, uitleg: string, regel: Gebruik = REGEL_TOOL): PaginaKennis =>
  ({ slug, pad, uitleg, velden: { hero_title: KOP_TOOL, hero_subtitle: regel, hero_image: FOTO } });

/* Wat de site per pagina leest: de bestanden onder src/app/(site) in de site-repo, tak v2 (70d5454),
 * stand 10-10-2026. De volgorde is die van de lijst in de CRM. `title` staat er niet bij: de site
 * toont die kolom nergens. Verandert de site, dan loopt deze lijst achter tot iemand hem bijwerkt. */
export const PAGINAS: PaginaKennis[] = [
  {
    slug: 'home', pad: '/', tekening: true,
    velden: {
      hero_title: toont('De grote kop in de opening van de homepage, zoals je hem typt. Leeg betekent: de vaste kop uit de code van de site.'),
      hero_subtitle: toont('De zin onder de kop in de opening. Leeg betekent: de vaste zin uit de code van de site.'),
      hero_image: toont('De grote foto in de opening van de homepage. Leeg betekent: geen foto, alleen de groene achtergrond.'),
    },
  },
  alleenFoto('over-ons', '/over-ons'),
  alleenFoto('energieoplossingen', '/energieoplossingen', FOTO_EN_LINK),
  alleenFoto('diensten', '/diensten', toont('De foto bovenaan de pagina, achter de kop, en het beeld bij een gedeelde link. Leeg betekent: geen foto bovenaan, en het logo bij een gedeelde link.')),
  alleenFoto('branches', '/branches', FOTO_EN_LINK),
  alleenFoto('projecten', '/projecten'),
  alleenFoto('nieuws', '/nieuws'),
  /* De pagina Kennisbank (besluit Stan, 10-10-2026). De site leest van deze rij alleen de foto:
   * kennisbank/page.tsx geeft `hero_image` door, met een vaste foto uit de code als die leeg is. De kop
   * en de regel eronder staan in de code van de site. De rij zelf komt uit het script
   * db/voorstel/2026-10-11-team-kennisbank-flex-e.sql in de site-repo; bestaat hij nog niet, dan staat
   * de pagina niet in de lijst (zonderRij). De kennisartikelen zijn een soort content (./website). */
  alleenFoto('kennisbank', '/kennisbank'),
  alleenFoto('vacatures', '/vacatures'),
  alleenFoto('contact', '/contact'),
  tool('tools', '/tools', 'De site toont deze pagina alleen als de rekentools aan staan (Instellingen).'),
  tool('ere-calculator', '/tools/ere-calculator', 'De site toont deze pagina alleen als deze rekentool aan staat (Instellingen).'),
  tool('diesel-vs-elektrisch', '/tools/diesel-vs-elektrisch', 'De site toont deze pagina alleen als deze rekentool aan staat (Instellingen).'),
  tool('flex-e-subsidie', '/tools/flex-e-subsidie', 'De site toont deze pagina alleen als deze rekentool aan staat (Instellingen).',
    toontNiet('De regel onder de kop volgt bij deze pagina de stand van de subsidieronde en komt uit de code van de site. Wat hier staat, toont de site niet.')),
  alleenFoto('privacy', '/privacyverklaring'),
  {
    slug: 'ons-bedrijf', pad: null,
    uitleg: 'De site toont deze pagina niet meer: het adres /ons-bedrijf stuurt door naar /over-ons, en de code van de site leest deze rij niet. Daarom is hier niets te wijzigen.',
    velden: { hero_title: NERGENS, hero_subtitle: NERGENS, hero_image: NERGENS },
  },
];

export const kennisVan = (slug: unknown): PaginaKennis | null => PAGINAS.find((p) => p.slug === slug) ?? null;

/** Is er op deze pagina iets te bewerken? Niet bij een pagina die de CRM niet kent, en niet bij een
 *  pagina die de site niet meer toont. */
export const isBewerkbaar = (kennis: PaginaKennis | null): boolean =>
  !!kennis && kennis.pad !== null && (!!kennis.tekening || VELDEN.some((v) => kennis.velden[v.kolom].toont));

export const ONBEKEND = 'De CRM weet niet wat de site met deze pagina doet: de slug staat niet in de lijst van de CRM. Daarom is hier niets te wijzigen.';

/** De slugs die de site opvraagt terwijl de rij er niet is. De site valt daar terug op haar vaste foto.
 *  Zo'n pagina staat niet in de lijst: de CRM maakt geen rij aan. De lijst zegt eronder welke er missen. */
export const zonderRij = (rijen: Rij[]): PaginaKennis[] => PAGINAS.filter((p) => p.pad !== null && !rijen.some((r) => r.slug === p.slug));

/* ── De inhoud (content) ───────────────────────────────────────────────────── */

type Voorwerp = Record<string, unknown>;
const isVoorwerp = (w: unknown): w is Voorwerp => typeof w === 'object' && w !== null && !Array.isArray(w);
const tekst = (w: unknown): string => (typeof w === 'string' ? w : '');
const soortNaam = (w: unknown): string => (typeof w === 'string' ? 'een losse tekst' : Array.isArray(w) ? 'een rij' : typeof w === 'number' ? 'een getal' : 'een losse waarde');

export interface Inhoud {
  /** false: `content` heeft een vorm die de bewerker niet kent, en wordt dan nooit geschreven. */
  ok: boolean;
  reden?: string;
  /** Het pad van de tekening, of leeg. Alleen bij een pagina met een tekening. */
  tekening: string;
  /** Wat er verder in `content` staat en hier niet te bewerken is, of null als er niets is. */
  rest: Voorwerp | null;
}

/** Leest `content`. Leeg (null) telt als een leeg object. */
export function leesInhoud(kennis: PaginaKennis | null, content: unknown): Inhoud {
  if (content == null) return { ok: true, tekening: '', rest: null };
  if (!isVoorwerp(content)) return { ok: false, reden: `Er staat geen object maar ${soortNaam(content)}.`, tekening: '', rest: null };
  const metTekening = !!kennis?.tekening;
  const { [TEKENING_SLEUTEL]: beeld, ...over } = content;
  const rest = metTekening ? over : content;
  if (metTekening && beeld != null && typeof beeld !== 'string') {
    return { ok: false, reden: `Bij "${TEKENING_SLEUTEL}" staat iets anders dan tekst.`, tekening: '', rest: null };
  }
  return { ok: true, tekening: metTekening ? tekst(beeld) : '', rest: Object.keys(rest).length ? rest : null };
}

/** Het pad van de tekening in het concept zetten. De rest van `content` blijft letterlijk staan. Een
 *  leeg pad haalt de sleutel weg: de site valt dan terug op het beeld van de eerste energieoplossing. */
export function zetTekening(content: unknown, pad: string): Voorwerp {
  const uit: Voorwerp = isVoorwerp(content) ? { ...content } : {};
  if (pad === '') delete uit[TEKENING_SLEUTEL];
  else uit[TEKENING_SLEUTEL] = pad;
  return uit;
}

/* ── Vergelijken, nakijken, schrijven ──────────────────────────────────────── */

/** Een tekst zoals hij geschreven wordt: zonder spaties aan de randen. Leeg en geen waarde zijn gelijk. */
const schoon = (w: unknown): string => tekst(w).trim();

/** De tekening zoals hij geschreven zou worden, of undefined als hij niet is gewijzigd of `content`
 *  hier niet te schrijven is (geen pagina met een tekening, of een onbekende vorm). */
function nieuweTekening(kennis: PaginaKennis, vorige: Rij, concept: Rij): string | undefined {
  if (!kennis.tekening) return undefined;
  const oud = leesInhoud(kennis, vorige[INHOUD]);
  const nieuw = leesInhoud(kennis, concept[INHOUD]);
  if (!oud.ok || !nieuw.ok) return undefined;
  return schoon(oud.tekening) === schoon(nieuw.tekening) ? undefined : schoon(nieuw.tekening);
}

/** De kolommen die geschreven moeten worden: alleen wat de site toont, is aangeraakt en echt anders
 *  is. Niets bij een pagina die niet te bewerken is. De slug en de naam staan er nooit in. */
export function wijziging(vorige: Rij, concept: Rij): Rij {
  const kennis = kennisVan(vorige.slug);
  const uit: Rij = {};
  if (!isBewerkbaar(kennis)) return uit;
  for (const { kolom } of VELDEN) {
    if (!kennis.velden[kolom].toont || !(kolom in concept)) continue;
    const nieuw = schoon(concept[kolom]);
    if (nieuw !== schoon(vorige[kolom])) uit[kolom] = nieuw === '' ? null : nieuw;
  }
  const beeld = nieuweTekening(kennis, vorige, concept);
  if (beeld !== undefined) uit[INHOUD] = zetTekening(vorige[INHOUD], beeld);
  return uit;
}

/** Wat er mis is, per kolom. Alleen wat is gewijzigd, wordt nagekeken: oude inhoud houdt het opslaan
 *  van een ander veld niet tegen. */
export function controleerPagina(vorige: Rij, concept: Rij): Controle {
  const fouten: Record<string, string> = {};
  const kennis = kennisVan(vorige.slug);
  if (isBewerkbaar(kennis)) {
    const anders = wijziging(vorige, concept);
    for (const { kolom, label, soort } of VELDEN) {
      if (soort !== 'afbeelding' || typeof anders[kolom] !== 'string') continue;
      const fout = keurBeeldpad(anders[kolom]);
      if (fout) fouten[kolom] = `${label}: ${fout}`;
    }
    const beeld = nieuweTekening(kennis, vorige, concept);
    const fout = beeld ? keurBeeldpad(beeld) : null;
    if (fout) fouten[TEKENING_FOUT] = `De tekening: ${fout}`;
  }
  return { ok: Object.keys(fouten).length === 0, fouten };
}

/* ── Lezen en opslaan ──────────────────────────────────────────────────────── */

function ofFout<T>(antwoord: { data: T | null; error: { message: string } | null }, wat: string): T {
  if (antwoord.error) throw new Error(`${wat}: ${antwoord.error.message}`);
  return antwoord.data as T;
}

/** De pagina's in de volgorde van de lijst hierboven; wat de CRM niet kent, staat achteraan op slug. */
export async function haalVastePaginas(client: WebsiteClient): Promise<Rij[]> {
  const rijen = ofFout<Rij[]>(await client.schema('website').from(TABEL).select('*').order('slug', { ascending: true }), "Pagina's lezen") ?? [];
  const plek = (rij: Rij): number => {
    const i = PAGINAS.findIndex((p) => p.slug === rij.slug);
    return i === -1 ? PAGINAS.length : i;
  };
  return [...rijen].sort((a, b) => plek(a) - plek(b) || String(a.slug).localeCompare(String(b.slug)));
}

export async function haalVastePagina(client: WebsiteClient, id: string): Promise<Rij | null> {
  return ofFout<Rij | null>(await client.schema('website').from(TABEL).select('*').eq('id', id).maybeSingle(), 'Pagina lezen');
}

export interface PaginaOpgeslagen {
  rij: Rij;
  /** De kolommen die zijn geschreven. Leeg = er was niets gewijzigd en er is niets geschreven. */
  geschreven: string[];
}

/** Slaat een pagina op: één update met alleen de gewijzigde kolommen. Er wordt hier nooit een rij
 *  aangemaakt of verwijderd. */
export async function slaPaginaOp(client: WebsiteClient, vorige: Rij, concept: Rij): Promise<PaginaOpgeslagen> {
  const kennis = kennisVan(vorige.slug);
  if (!isBewerkbaar(kennis)) throw new Error(kennis?.uitleg ?? ONBEKEND);
  const controle = controleerPagina(vorige, concept);
  if (!controle.ok) throw new Error(Object.values(controle.fouten)[0]);
  const anders = wijziging(vorige, concept);
  const geschreven = Object.keys(anders);
  if (geschreven.length === 0) return { rij: vorige, geschreven };
  const antwoord = await client.schema('website').from(TABEL).update(anders).eq('id', vorige.id).select('*').maybeSingle();
  const rij = ofFout<Rij | null>(antwoord, 'Pagina opslaan');
  // RLS weigert zonder fout: nul rijen terug betekent dat er niets is opgeslagen.
  if (!rij) throw new Error('De pagina is niet opgeslagen. Je hebt hier geen schrijfrecht, of de rij bestaat niet meer.');
  return { rij, geschreven };
}

/** Het adres van een pagina op de site, of null als de site hem niet toont of de CRM hem niet kent. */
export const padOpSite = (rij: Rij): string | null => kennisVan(rij.slug)?.pad ?? null;
